/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * BarMeter — thin horizontal fill bar showing a value as a fraction of
 * a max (defaults to 100). Used inside stat tiles and table cells so
 * users can eyeball "is this high or low" without reading digits.
 *
 * Two flavors:
 *
 *   <BarMeter value={62} />                       // 62% fill, tier color
 *   <BarMeter value={62} baseline={48} />         // adds a tick at 48
 *   <BarMeter value={62} tierColor={false} />     // neutral red fill
 *   <BarMeter value={62} showValue />             // renders "62" to right
 *
 * When `tierColor` is true (default), the fill color follows the tier
 * mapping — 75+ = gold, 60+ = red, 40+ = orange, <40 = blue. This makes
 * the bar double as a tier indicator.
 */
import React from 'react';
import { tierFromScore } from '@/lib/tiers';

type Props = {
  value: number | null | undefined;
  max?: number;
  /** Optional tick mark for a baseline (e.g. county average). */
  baseline?: number | null;
  /** Color the fill by tier (default). Set false for a neutral red fill. */
  tierColor?: boolean;
  /** Explicit fill color override (CSS hex). Wins over tierColor. */
  color?: string;
  /** Show the numeric value to the right of the bar. */
  showValue?: boolean;
  /** Suffix for the shown value (e.g. '%'). Only used when showValue is true. */
  suffix?: string;
  className?: string;
  /** Height in pixels; default 4. */
  height?: number;
};

export const BarMeter: React.FC<Props> = ({
  value,
  max = 100,
  baseline,
  tierColor = true,
  color,
  showValue = false,
  suffix = '',
  className = '',
  height = 4,
}) => {
  if (value == null || Number.isNaN(value)) {
    return (
      <div
        className={`relative bg-bg-elevated/50 rounded-sm ${className}`}
        style={{ height }}
      />
    );
  }

  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const baselinePct =
    baseline != null && !Number.isNaN(baseline)
      ? Math.max(0, Math.min(100, (baseline / max) * 100))
      : null;

  const fill = color
    ? color
    : tierColor
    ? tierFromScore((value / max) * 100).hex
    : '#dc2626';

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      <div
        className="relative flex-1 bg-bg-elevated/50 rounded-sm overflow-hidden"
        style={{ height }}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-sm transition-[width]"
          style={{ width: `${pct}%`, backgroundColor: fill }}
        />
        {baselinePct != null && (
          <div
            className="absolute inset-y-0 w-px bg-fg-secondary/70"
            style={{ left: `${baselinePct}%` }}
            title={`Baseline: ${baseline!.toFixed(1)}`}
          />
        )}
      </div>
      {showValue && (
        <span className="text-[10px] text-fg-secondary tabular-nums whitespace-nowrap">
          {value.toFixed(value >= 10 ? 0 : 1)}
          {suffix}
        </span>
      )}
    </div>
  );
};

export default BarMeter;
